import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { compressImage } from "../lib/imageCompressor";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  QrCode,
  Camera,
  Loader2,
  Building2,
  CheckCircle2,
  Globe,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import { Html5QrcodeScanner } from "html5-qrcode";

import { translations, getCategoriesByType } from "../components/public-qr/translations";
import { SuccessScreen } from "../components/public-qr/SuccessScreen";
import { OfficeServiceChooser, ClinicServiceChooser } from "../components/public-qr/ServiceChooser";
import { MaintenanceForm } from "../components/public-qr/MaintenanceForm";
import { SterilizationForm } from "../components/public-qr/SterilizationForm";
import { MedicalReportForm } from "../components/public-qr/MedicalReportForm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PublicQRScanner = ({ locationType: propLocationType }) => {
  const { clinicId, locationId } = useParams();
  const navigate = useNavigate();

  // Core state
  const [lang, setLang] = useState("en");
  const [scanning, setScanning] = useState(false);
  const [scannedLocation, setScannedLocation] = useState(null);
  const [locationType, setLocationType] = useState(propLocationType || "clinic");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showServiceChoice, setShowServiceChoice] = useState(false);
  const [requestType, setRequestType] = useState(null); // 'maintenance' | 'sterilization'

  // Maintenance state
  const [images, setImages] = useState([]);
  const [clinicMedicalDevices, setClinicMedicalDevices] = useState([]);
  const [clinicElectronicDevices, setClinicElectronicDevices] = useState([]);
  const [formData, setFormData] = useState({
    element_type: "",
    category: "",
    description: "",
    priority: "medium",
    reporter_name: "",
    reporter_phone: "",
    reporter_whatsapp: "",
  });

  // Sterilization state
  const [instrumentsCatalog, setInstrumentsCatalog] = useState([]);
  const [selectedInstruments, setSelectedInstruments] = useState({});
  const [activeCategory, setActiveCategory] = useState(null);
  const [boxPhoto, setBoxPhoto] = useState(null);
  const [sterileNotes, setSterileNotes] = useState("");
  const [sterileResult, setSterileResult] = useState(null);

  // Medical Report state
  const [medicalReportForm, setMedicalReportForm] = useState({
    patient_first_name: "",
    patient_father_name: "",
    patient_family_name: "",
    medical_file_number: "",
    treating_doctor_id: "",
    treating_doctor_name: "",
    treating_doctor_specialty: "",
    requester_name: "",
    requester_whatsapp: "",
  });
  const [doctors, setDoctors] = useState([]);
  const [medicalReportResult, setMedicalReportResult] = useState(null);

  const scannerRef = useRef(null);
  const t = translations[lang];
  const isRTL = lang === "ar";

  useEffect(() => {
    if (clinicId) {
      setLocationType("clinic");
      fetchLocationById("clinic", clinicId);
    } else if (locationId && propLocationType) {
      setLocationType(propLocationType);
      fetchLocationById(propLocationType, locationId);
    }
    return () => {
      if (scannerRef.current) {
        scannerRef.current.clear().catch(console.error);
      }
    };
  }, [clinicId, locationId, propLocationType]);

  const toggleLanguage = () => setLang(lang === "ar" ? "en" : "ar");

  const getLocationTypeName = (type) => {
    const names = {
      ar: { clinic: "عيادة", office: "مكتب", restroom: "دورة مياه", facility: "مرفق" },
      en: { clinic: "Clinic", office: "Office", restroom: "Restroom", facility: "Facility" },
    };
    return names[lang][type] || type;
  };

  const fetchLocationById = async (type, id) => {
    setLoading(true);
    try {
      const endpoint =
        type === "clinic" ? `public/clinic/${id}` : `public/location/${type}/${id}`;
      const response = await axios.get(`${API}/${endpoint}`);
      setScannedLocation(response.data);
      setLocationType(type);

      // Preload biomed devices whenever the scanned location supports them.
      const supportsBiomed =
        type === "clinic" ||
        ((type === "facility" || type === "office") && response.data?.has_biomed_devices === true);
      if (supportsBiomed) {
        try {
          const devRes = await axios.get(`${API}/biomed-devices/public/${type}/${response.data.id}`);
          const all = devRes.data || [];
          setClinicMedicalDevices(all.filter((d) => !d.device_type || d.device_type === "biomed"));
          setClinicElectronicDevices(all.filter((d) => d.device_type === "electrical"));
        } catch {
          setClinicMedicalDevices([]);
          setClinicElectronicDevices([]);
        }
      } else {
        setClinicMedicalDevices([]);
        setClinicElectronicDevices([]);
      }

      if (type === "restroom") {
        setFormData((prev) => ({ ...prev, element_type: "structural", category: "" }));
        setRequestType("maintenance");
      } else if (type === "facility" || type === "office") {
        if (type === "facility") setFormData((prev) => ({ ...prev, element_type: "", category: "" }));
        const showTP = type === "office" && response.data?.show_treatment_plan === true;
        const showMR = response.data?.show_medical_report === true;
        if (showTP || showMR) {
          setShowServiceChoice(true);
        } else {
          setRequestType("maintenance");
        }
      }
      // For clinics we leave requestType = null so the user picks Maintenance vs Sterilization
    } catch (error) {
      toast.error(t.clinicNotFound);
    } finally {
      setLoading(false);
    }
  };

  const startScanner = () => {
    setScanning(true);
    setTimeout(() => {
      const scanner = new Html5QrcodeScanner("qr-reader-public", {
        qrbox: { width: 250, height: 250 },
        fps: 5,
      });
      scanner.render(onScanSuccess, () => {});
      scannerRef.current = scanner;
    }, 100);
  };

  const stopScanner = () => {
    if (scannerRef.current) {
      scannerRef.current.clear().catch(console.error);
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const onScanSuccess = async (decodedText) => {
    stopScanner();
    let scannedId = null;
    let scannedType = "clinic";

    if (decodedText.includes("/report/")) {
      const urlParts = decodedText.split("/report/");
      const pathPart = urlParts[1];
      if (pathPart.startsWith("office/")) {
        scannedType = "office";
        scannedId = pathPart.replace("office/", "");
      } else if (pathPart.startsWith("restroom/")) {
        scannedType = "restroom";
        scannedId = pathPart.replace("restroom/", "");
      } else if (pathPart.startsWith("facility/")) {
        scannedType = "facility";
        scannedId = pathPart.replace("facility/", "");
      } else {
        scannedId = pathPart;
      }
    } else {
      const match = decodedText.match(/^clinic:(.+)$/);
      if (match) scannedId = match[1];
    }

    if (!scannedId) {
      toast.error(t.invalidQR);
      return;
    }
    fetchLocationById(scannedType, scannedId);
  };

  // ==================== MAINTENANCE ====================
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length + images.length > 5) {
      toast.error(t.maxPhotosError);
      return;
    }
    for (const file of files) {
      const compressed = await compressImage(file);
      const formDataUpload = new FormData();
      formDataUpload.append("file", compressed);
      try {
        const response = await axios.post(`${API}/public/upload`, formDataUpload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setImages((prev) => [...prev, response.data.data]);
      } catch (error) {
        toast.error(t.uploadFailed);
      }
    }
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !scannedLocation ||
      !formData.element_type ||
      !formData.category ||
      !formData.description ||
      !formData.reporter_name ||
      !formData.reporter_whatsapp
    ) {
      toast.error(t.fillRequired);
      return;
    }
    if (images.length === 0) {
      toast.error(isRTL ? "يرجى إرفاق صورة واحدة على الأقل" : "Please attach at least one photo");
      return;
    }

    setSubmitting(true);
    try {
      const requestData = {
        element_type: formData.element_type,
        category: formData.category,
        description: formData.description,
        priority: formData.priority,
        reporter_name: formData.reporter_name,
        reporter_phone: "",
        reporter_whatsapp: `+966${formData.reporter_whatsapp}`,
        images,
        lang,
      };
      if (locationType === "clinic") {
        requestData.clinic_id = scannedLocation.id;
      } else {
        requestData.location_type = locationType;
        requestData.location_id = scannedLocation.id;
        requestData.location_name = scannedLocation.name;
      }
      await axios.post(`${API}/public/maintenance`, requestData);
      setSubmitted(true);
      toast.success(t.submitSuccess);
    } catch (error) {
      toast.error(t.submitFailed);
    } finally {
      setSubmitting(false);
    }
  };

  // ==================== STERILIZATION ====================
  const loadInstrumentsCatalog = async () => {
    try {
      let items = [];
      try {
        const own = await axios.get(`${API}/instruments/public/clinic/${scannedLocation?.id}/own`);
        items = own.data || [];
      } catch { items = []; }
      if (items.length === 0) {
        const res = await axios.get(`${API}/instruments/public/catalog`);
        items = res.data || [];
      }
      setInstrumentsCatalog(items);
    } catch {
      toast.error(isRTL ? "فشل تحميل قائمة الأدوات" : "Failed to load instruments");
    }
  };

  const handleBoxPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await compressImage(file);
      const fd = new FormData();
      fd.append("file", compressed);
      const res = await axios.post(`${API}/public/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBoxPhoto(res.data.data);
    } catch {
      toast.error(t.uploadFailed);
    }
  };

  const toggleInstrument = (id) => {
    setSelectedInstruments((prev) => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = true;
      return next;
    });
  };

  const handleSterilizationSubmit = async (e) => {
    e.preventDefault();
    if (!scannedLocation) return;
    const picked = instrumentsCatalog.filter((i) => selectedInstruments[i.id]);
    if (picked.length === 0) {
      toast.error(isRTL ? "يرجى اختيار أداة واحدة على الأقل" : "Please select at least one instrument");
      return;
    }
    if (!boxPhoto) {
      toast.error(isRTL ? "صورة الصندوق مطلوبة" : "Box photo is required");
      return;
    }
    if (!formData.reporter_name || !formData.reporter_whatsapp) {
      toast.error(isRTL ? "الاسم ورقم الواتساب مطلوبان" : "Name and WhatsApp are required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        clinic_id: scannedLocation.id,
        reporter_name: formData.reporter_name,
        reporter_whatsapp: `+966${formData.reporter_whatsapp}`,
        instruments: picked.map((i) => ({
          instrument_id: i.id,
          name_en: i.name_en,
          name_ar: i.name_ar || "",
        })),
        box_photo: boxPhoto,
        notes: sterileNotes,
        lang,
      };
      const res = await axios.post(`${API}/sterilization/public/requests`, payload);
      setSterileResult(res.data);
      setSubmitted(true);
      toast.success(isRTL ? "تم إرسال الطلب" : "Request submitted");
    } catch (err) {
      toast.error(err.response?.data?.detail || (isRTL ? "فشل الإرسال" : "Submit failed"));
    } finally {
      setSubmitting(false);
    }
  };

  // ==================== MEDICAL REPORT ====================
  const loadDoctors = async () => {
    try {
      const res = await axios.get(`${API}/doctors`);
      // /api/doctors returns { specialties, doctors }
      setDoctors(res.data?.doctors || []);
    } catch {
      toast.error(isRTL ? "فشل تحميل قائمة الأطباء" : "Failed to load doctors");
    }
  };

  const handleMedicalReportSubmit = async (e) => {
    e.preventDefault();
    if (!scannedLocation) return;
    const f = medicalReportForm;
    if (
      !f.patient_first_name ||
      !f.patient_father_name ||
      !f.patient_family_name ||
      !f.medical_file_number ||
      !f.treating_doctor_id ||
      !f.requester_name ||
      !f.requester_whatsapp
    ) {
      toast.error(isRTL ? "يرجى تعبئة جميع الحقول" : "Please fill all fields");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...(locationType === "clinic"
          ? { clinic_id: scannedLocation.id }
          : { location_type: locationType, location_id: scannedLocation.id }),
        patient_first_name: f.patient_first_name.trim(),
        patient_father_name: f.patient_father_name.trim(),
        patient_family_name: f.patient_family_name.trim(),
        medical_file_number: f.medical_file_number.trim(),
        treating_doctor_id: f.treating_doctor_id,
        treating_doctor_name: f.treating_doctor_name || "",
        treating_doctor_specialty: f.treating_doctor_specialty || "",
        requester_name: f.requester_name.trim(),
        requester_whatsapp: `+966${f.requester_whatsapp}`,
        lang,
      };
      const res = await axios.post(`${API}/medical-reports/public`, payload);
      setMedicalReportResult(res.data);
      setSubmitted(true);
      toast.success(isRTL ? "تم إرسال الطلب" : "Request submitted");
    } catch (err) {
      toast.error(err.response?.data?.detail || (isRTL ? "فشل الإرسال" : "Submit failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setScannedLocation(null);
    setLocationType("clinic");
    setSubmitted(false);
    setRequestType(null);
    setShowServiceChoice(false);
    setSelectedInstruments({});
    setBoxPhoto(null);
    setSterileNotes("");
    setSterileResult(null);
    setClinicMedicalDevices([]);
    setClinicElectronicDevices([]);
    setActiveCategory(null);
    setMedicalReportForm({
      patient_first_name: "",
      patient_father_name: "",
      patient_family_name: "",
      medical_file_number: "",
      treating_doctor_id: "",
      treating_doctor_name: "",
      treating_doctor_specialty: "",
      requester_name: "",
      requester_whatsapp: "",
    });
    setMedicalReportResult(null);
    setFormData({
      element_type: "",
      category: "",
      description: "",
      priority: "medium",
      reporter_name: "",
      reporter_phone: "",
      reporter_whatsapp: "",
    });
    setImages([]);
  };

  const categories = getCategoriesByType(formData.element_type, t);

  if (loading) {
    return (
      <div
        className="min-h-screen bg-[#F8F7FC] flex items-center justify-center"
        dir={isRTL ? "rtl" : "ltr"}
      >
        <Loader2 className="w-8 h-8 animate-spin text-[#655dc6]" />
      </div>
    );
  }

  if (submitted) {
    return (
      <>
        <Toaster position="top-center" richColors />
        <SuccessScreen
          isRTL={isRTL}
          t={t}
          sterileResult={sterileResult}
          medicalReportResult={medicalReportResult}
          onReset={resetForm}
        />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F7FC]" dir={isRTL ? "rtl" : "ltr"}>
      <Toaster position="top-center" richColors />

      {/* Header */}
      <div className="bg-[#655dc6] text-white py-4 px-4">
        <div className="max-w-3xl mx-auto">
          <div className={`flex ${isRTL ? "justify-start" : "justify-end"} mb-3`}>
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-sm"
              data-testid="language-toggle"
            >
              <Globe className="w-4 h-4" />
              <span>{lang === "ar" ? "English" : "العربية"}</span>
            </button>
          </div>

          <div className={`flex items-center gap-4 ${isRTL ? "" : "flex-row-reverse justify-end"}`}>
            <div className="bg-white rounded-lg p-1 flex-shrink-0">
              <img src="/lucent-logo.png" alt="Lucent" className="w-10 h-10 object-contain" />
            </div>
            <div className={isRTL ? "" : "text-right"}>
              <h1 className="text-xl font-bold">{t.headerTitle}</h1>
              {t.headerSubtitle && <p className="text-sm text-white/80">{t.headerSubtitle}</p>}
            </div>
          </div>
        </div>
      </div>

      <div className={`${scannedLocation && showServiceChoice ? "max-w-3xl" : "max-w-lg"} mx-auto p-4 space-y-6`}>
        {scannedLocation && showServiceChoice ? (
          <OfficeServiceChooser
            scannedLocation={scannedLocation}
            lang={lang}
            showTreatmentPlan={locationType === "office" && scannedLocation?.show_treatment_plan === true}
            showMedicalReport={scannedLocation?.show_medical_report === true}
            onMaintenance={() => {
              setShowServiceChoice(false);
              setRequestType("maintenance");
            }}
            onMedicalReport={() => {
              setShowServiceChoice(false);
              setRequestType("medical_report");
              loadDoctors();
            }}
            onTreatmentPlan={() => navigate("/treatment-plan")}
            onConsents={() => navigate("/consents")}
            onDoctors={() => navigate("/doctors")}
          />
        ) : !scannedLocation ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-12">
              {!scanning ? (
                <div className="text-center">
                  <div className="w-24 h-24 rounded-2xl bg-[#F5F3F9] flex items-center justify-center mx-auto mb-6">
                    <QrCode className="w-12 h-12 text-[#655dc6]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#4a3fa8] mb-2">{t.scanQR}</h3>
                  <p className="text-slate-500 mb-6">{t.scanDescription}</p>
                  <Button
                    onClick={startScanner}
                    className="bg-[#655dc6] hover:bg-[#5449a8] text-white"
                    data-testid="start-public-scan-btn"
                  >
                    <Camera className="w-5 h-5 mx-2" />
                    {t.startScan}
                  </Button>
                </div>
              ) : (
                <div className="text-center">
                  <div id="qr-reader-public" className="mb-4"></div>
                  <Button variant="outline" onClick={stopScanner} className="mt-4">
                    {t.cancelScan}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Scanned Location Info */}
            <Card
              className={`border-0 shadow-sm ${
                isRTL ? "border-r-4 border-r-[#9b8fd4]" : "border-l-4 border-l-[#9b8fd4]"
              }`}
            >
              <CardContent className="pt-6">
                <div className={`flex items-center gap-4 ${isRTL ? "" : "flex-row-reverse"}`}>
                  <div className="w-14 h-14 rounded-xl bg-[#F5F3F9] flex items-center justify-center">
                    <Building2 className="w-7 h-7 text-[#655dc6]" />
                  </div>
                  <div className={`flex-1 ${isRTL ? "" : "text-right"}`}>
                    <p className="text-xs text-[#9b8fd4] mb-1">{getLocationTypeName(locationType)}</p>
                    <h3 className="font-bold text-lg text-[#4a3fa8]">{scannedLocation.name}</h3>
                    <p className="text-slate-500">
                      {scannedLocation.floor} • {scannedLocation.building}
                    </p>
                  </div>
                  <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                </div>
              </CardContent>
            </Card>

            {/* Clinic-only chooser: Maintenance vs Sterilization vs Medical Report */}
            {locationType === "clinic" && requestType === null && (
              <ClinicServiceChooser
                isRTL={isRTL}
                onPickMaintenance={() => setRequestType("maintenance")}
                onPickSterilization={() => {
                  setRequestType("sterilization");
                  loadInstrumentsCatalog();
                }}
                onPickMedicalReport={() => {
                  setRequestType("medical_report");
                  loadDoctors();
                }}
                onPickInstruments={() => navigate(`/instruments-request/${scannedLocation.id}`)}
              />
            )}

            {requestType === "medical_report" && (
              <MedicalReportForm
                isRTL={isRTL}
                formData={medicalReportForm}
                setFormData={setMedicalReportForm}
                doctors={doctors}
                submitting={submitting}
                handleSubmit={handleMedicalReportSubmit}
                onBack={
                  locationType === "clinic"
                    ? () => setRequestType(null)
                    : () => { setRequestType(null); setShowServiceChoice(true); }
                }
              />
            )}

            {requestType === "sterilization" && (
              <SterilizationForm
                t={t}
                isRTL={isRTL}
                formData={formData}
                setFormData={setFormData}
                instrumentsCatalog={instrumentsCatalog}
                selectedInstruments={selectedInstruments}
                setSelectedInstruments={setSelectedInstruments}
                toggleInstrument={toggleInstrument}
                activeCategory={activeCategory}
                setActiveCategory={setActiveCategory}
                boxPhoto={boxPhoto}
                handleBoxPhotoUpload={handleBoxPhotoUpload}
                sterileNotes={sterileNotes}
                setSterileNotes={setSterileNotes}
                submitting={submitting}
                handleSterilizationSubmit={handleSterilizationSubmit}
                onBack={() => setRequestType(null)}
              />
            )}

            {requestType === "maintenance" && (
              <MaintenanceForm
                t={t}
                isRTL={isRTL}
                locationType={locationType}
                clinicMedicalDevices={clinicMedicalDevices}
                clinicElectronicDevices={clinicElectronicDevices}
                formData={formData}
                setFormData={setFormData}
                categories={categories}
                images={images}
                handleImageUpload={handleImageUpload}
                removeImage={removeImage}
                submitting={submitting}
                handleSubmit={handleSubmit}
                onBack={
                  locationType === "clinic"
                    ? () => setRequestType(null)
                    : (locationType === "office" && scannedLocation?.show_treatment_plan) || scannedLocation?.show_medical_report
                    ? () => { setRequestType(null); setShowServiceChoice(true); }
                    : undefined
                }
              />
            )}
          </>
        )}

        {/* Footer */}
        <p className="text-center text-sm text-slate-400 pb-4">{t.copyright}</p>
      </div>
    </div>
  );
};

export default PublicQRScanner;
